import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Spin, Image } from 'antd';
import { useTranslation } from 'react-i18next';
import { Icon } from 'Components';
import StepFooter from '../../components/StepFooter';
import DebounceInput from '../../components/DebouncedInput';
import TemplateCard from '../../components/TemplateCard';
import PaginationElement from '../../components/Pagination';
import EmptyState from '../../components/EmptyState';
import { 
  getPhishingCampaignCategories, 
  getPhishingCampaignTemplates
} from '../../service/index.Service';
import { errorMessageHandler } from '../../utils';
import styles from './CategoryList.module.scss';

export interface CategoryData {
  id: number;
  name: string;
  templates_count?: number;
  description?: string;
  img_path?: string;
}

export interface TemplateData {
  id: number | string;
  title: string;
  difficulty: string;
  img_path: string;
  categories?: string[];
}

interface CategoryListProps {
  tenantId: string | number | undefined;
  onSelectCategory: (categoryName: string) => void;
  onSelect?: (value: string | number | null) => void;
  onBack: () => void;
  onNext: () => void;
  setToast: (toast: { message: string; type: 'warning' | 'success' }) => void;
}

const FALLBACK_ICON = 'https://placehold.co/18x18';
const CATEGORIES_PAGE_SIZE = 3;
const TEMPLATES_PAGE_SIZE = 12;

export const CategoryList: React.FC<CategoryListProps> = ({
  tenantId,
  onSelectCategory,
  onSelect,
  onBack,
  onNext,
  setToast
}) => {
  const { t } = useTranslation();
  
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  
  const [categoriesCurrentPage, setCategoriesCurrentPage] = useState<number>(1);
  const [templatesCurrentPage, setTemplatesCurrentPage] = useState<number>(1);
  const [totalTemplates, setTotalTemplates] = useState<number>(0);
  
  const [loading, setLoading] = useState<boolean>(false);

  const fetchedCategoriesRef = useRef<CategoryData[] | null>(null);

  const fetchCategories = useCallback(async () => {
    if (fetchedCategoriesRef.current !== null) {
      setCategories(fetchedCategoriesRef.current);
      return;
    }

    try {
      setLoading(true);
      const res = await getPhishingCampaignCategories(tenantId ?? 0);
      let categoriesData: CategoryData[] = [];

      if (res && res.data) {
        categoriesData = res.data;
      } else if (Array.isArray(res)) {
        categoriesData = res;
      }

      setCategories(categoriesData);
      fetchedCategoriesRef.current = categoriesData;
    } catch (error: any) {
      const toastMessage = errorMessageHandler(
        error,
        'phishingManager.errors.fetchCategories'
      );
      setToast({ message: t(toastMessage), type: 'warning' });
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, t, setToast]);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPhishingCampaignTemplates(tenantId ?? 0, {
        name: debouncedSearch.trim().length > 0 ? debouncedSearch : undefined,
        status: 'active',
        sort: 'name',
        order: 'asc',
        page: templatesCurrentPage,
        limit: TEMPLATES_PAGE_SIZE
      });

      if (response && response.data) {
        const mappedData: TemplateData[] = response.data.map((item: any) => ({
          id: item.id,
          title: item.name,
          difficulty: item.difficulties || item.difficulty || t('phishingTemplateManager.intermediate', 'Intermediate'),
          img_path: item.img_path,
          categories: Array.isArray(item.categories) ? item.categories : []
        }));

        setTemplates(mappedData);
        if (response.meta) {
          setTotalTemplates(response.meta.total);
        }
      }
    } catch (error: any) {
      const toastMessage = errorMessageHandler(
        error,
        'phishingManager.errors.fetchTemplates'
      );
      setToast({ message: t(toastMessage), type: 'warning' });
      setTemplates([]);
      setTotalTemplates(0);
    } finally {
      setLoading(false);
    }
  }, [tenantId, debouncedSearch, templatesCurrentPage, t, setToast]);

  useEffect(() => {
    fetchedCategoriesRef.current = null;
    setCategoriesCurrentPage(1);
    setTemplatesCurrentPage(1);
  }, [tenantId]);

  useEffect(() => {
    setTemplatesCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (debouncedSearch.trim().length > 0) {
      fetchTemplates();
    } else {
      fetchCategories();
      setTemplates([]);
      setTotalTemplates(0);
    }
  }, [debouncedSearch, templatesCurrentPage, fetchCategories, fetchTemplates]);

  const isSearchActive = debouncedSearch.trim().length > 0;

  const paginatedCategories = categories.slice(
    (categoriesCurrentPage - 1) * CATEGORIES_PAGE_SIZE,
    categoriesCurrentPage * CATEGORIES_PAGE_SIZE
  );

  const handleBackToAllCategories = () => {
    setDebouncedSearch('');
    setTemplates([]);
    setTotalTemplates(0);
    setTemplatesCurrentPage(1);
    setCategoriesCurrentPage(1);
  };

  const handleTemplateCardClick = (templateId: number | string) => {
    if (onSelect) {
      onSelect(templateId);
    }
  };

  return (
    <Spin spinning={loading}>
      <div className={`${styles.campaignScenarioContainer} step-card`}>
        <div className={styles.scenarioHeader}>
          <div className={styles.scenarioTitle}>
            {t('phishingManager.campaignSetup.step1.title', 'Scenario')}
          </div>
          <div className={styles.scenarioSubtitle}>
            {t('phishingManager.campaignSetup.step1.subtitle', 'Choose the scenario for the campaign')}
          </div>
        </div>

        <div className={styles.scenarioCategoriesSection}>
          {isSearchActive && (
            <div className={styles.breadcrumbWrapper}>
              <span className={styles.breadcrumbLink} onClick={handleBackToAllCategories}>
                {t('phishingTemplateManager.allTemplates', 'All Templates')}
              </span>
            </div>
          )}

          <div className={styles.toolbarHeaderRow}>
            <div 
              className={styles.allTemplatesBadge}
              onClick={!isSearchActive ? () => onSelectCategory('') : undefined}
            >
              <div className={styles.listIconWrapper}>
                <Icon type="listColored" />
              </div>
              <span className={styles.allTemplatesText}>
                {isSearchActive 
                  ? `${t('phishingTemplateManager.allTemplates', 'All Templates')} (${totalTemplates})`
                  : `${t('phishingTemplateManager.categories', 'Categories')} (${categories.length})`}
              </span>
            </div>

            <div className={styles.searchWrapper}>
              <DebounceInput
                debounceSearch={debouncedSearch}
                placeholder={t('phishingTemplateManager.searchPlaceholder', 'Search')}
                setDebouncedSearch={setDebouncedSearch}
                className="phishing-custom-debounce-search"
                showClearOption
              />
            </div>
          </div>

          {!isSearchActive ? (
            <>
              <div className={styles.categoriesGrid}>
                {paginatedCategories.map((cat) => (
                  <div 
                    key={cat.id} 
                    className={styles.categoryCard}
                    onClick={() => onSelectCategory(cat.name)}
                  >
                    <div className={styles.cardTopRow}>
                      <div className={styles.categoryIconBg}>
                        <div className={styles.iconCircle}>
                          <Image
                            className={styles.categoryImg}
                            src={cat.img_path || FALLBACK_ICON}
                            fallback={FALLBACK_ICON}
                            alt={cat.name}
                            preview={false}
                          />
                        </div>
                      </div>
                    </div>

                    <div className={styles.cardTitleRow}>
                      <span className={styles.categoryTitle}>{cat.name}</span>
                      <div className={styles.countBadge}>
                        <span className={styles.countText}>
                          {cat.templates_count ?? 0} templates
                        </span>
                      </div>
                    </div>

                    <p className={styles.categoryDescription}>
                      {cat.description || t('phishingManager.campaignSetup.step1.defaultCategoryDesc', 'Phishing simulations covering various attack scenarios.')}
                    </p>

                    <div className={styles.cardFooterAction}>
                      <div className={styles.actionBtn}>
                        <span className={styles.actionText}>
                          {t('phishingTemplateManager.viewTemplates', 'View Templates')}
                        </span>
                        <Icon type="arrowRight" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {categories.length > CATEGORIES_PAGE_SIZE && (
                <div className={styles.paginationWrapper}>
                  <PaginationElement
                    total={categories.length}
                    pageSize={CATEGORIES_PAGE_SIZE}
                    current={categoriesCurrentPage}
                    onChange={(page) => setCategoriesCurrentPage(page)}
                  />
                </div>
              )}
            </>
          ) : (
            <div className={styles.templatesContentArea}>
              {templates.length === 0 && !loading ? (
                <div className={styles.emptyContainer}>
                  <EmptyState
                    title={t('phishingTemplateManager.noTemplatesMatch', 'No templates match your filters.')}
                    description={[]}
                    actionLabel={null}
                  />
                </div>
              ) : (
                <>
                  <div className={styles.templatesGrid}>
                    {templates.map((template) => (
                      <TemplateCard
                        key={template.id}
                        selected={false}
                        isLibrary={true}
                        onSelect={() => handleTemplateCardClick(template.id)}
                        template={{
                          value: template.id,
                          label: template.title,
                          difficulty: template.difficulty,
                          img: template.img_path,
                          categories: template.categories || [],
                          languages: []
                        }}
                      />
                    ))}
                  </div>

                  {totalTemplates > TEMPLATES_PAGE_SIZE && (
                    <div className={styles.paginationWrapper}>
                      <PaginationElement
                        total={totalTemplates}
                        pageSize={TEMPLATES_PAGE_SIZE}
                        current={templatesCurrentPage}
                        onChange={(page) => setTemplatesCurrentPage(page)}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.categoryFooterWrapper}>
        <StepFooter
          onBack={onBack}
          onNext={onNext}
          nextBtnDisabled={true}
        />
      </div>
    </Spin>
  );
};

export default CategoryList;